// Opções de modelo/max_tokens por fase — mantém sincronia com
// src/services/claude.ts e src/routes/settings.ts (backend).
import type { ModelProfile } from './types'

export interface ModelOption {
  value: string
  label: string
  provider: 'anthropic' | 'gemini'
}

export const MODEL_OPTIONS: ModelOption[] = [
  { value: 'claude-haiku-4-5', label: 'Claude Haiku 4.5 ($1/$5)', provider: 'anthropic' },
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 ($3/$15)', provider: 'anthropic' },
  { value: 'claude-sonnet-5', label: 'Claude Sonnet 5 ($2/$10)', provider: 'anthropic' },
  { value: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5 ($2/$10)', provider: 'anthropic' },
  { value: 'claude-opus-5', label: 'Claude Opus 5 ($5/$25)', provider: 'anthropic' },
  { value: 'claude-opus-5-5', label: 'Claude Opus 5.5 ($4/$20)', provider: 'anthropic' },
  { value: 'claude-fable-5', label: 'Claude Fable 5 ($10/$50)', provider: 'anthropic' },
  { value: 'gemini-3.8-flash', label: 'Gemini 3.8 Flash', provider: 'gemini' },
  { value: 'gemini-3.5-flash', label: 'Gemini 3.5 Flash', provider: 'gemini' },
  { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash', provider: 'gemini' },
  { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash', provider: 'gemini' },
  { value: 'gemini-1.5-flash', label: 'Gemini 1.5 Flash', provider: 'gemini' },
  { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro', provider: 'gemini' },
]

export const MAX_TOKENS_OPTIONS = [1024, 2048, 4096, 8192, 16384]

/** Rótulo de um id de modelo (o próprio id se não estiver na lista). */
export function modelLabel(value: string | undefined): string {
  return MODEL_OPTIONS.find((m) => m.value === value)?.label ?? value ?? '—'
}

// Perfis de modelo da análise semanal — espelham MODEL_PROFILES (src/services/claude.ts).
// O cliente escolhe o perfil; o modelo e o raciocínio de cada perfil ficam em Configurações
// (profile_<perfil>_model / _effort). Os defaults são os da migration, só para exibir.
export const MODEL_PROFILES: Array<{
  value: ModelProfile
  label: string
  defaultModel: string
  defaultEffort: string
}> = [
  { value: 'simples', label: 'Simples', defaultModel: 'claude-sonnet-5', defaultEffort: 'high' },
  { value: 'medio', label: 'Médio', defaultModel: 'claude-sonnet-5-5', defaultEffort: 'high' },
  { value: 'avancado', label: 'Avançado', defaultModel: 'claude-opus-5-5', defaultEffort: 'high' },
]

// Trava do loop de tools do chat "Falar sobre" (rodadas de modelo por resposta).
export const TOOL_STEPS_OPTIONS = [3, 4, 6, 8, 10, 12, 16, 20]

// Nível de raciocínio (thinking) — afeta os modelos Claude e o Gemini 3.8 Flash (que só
// tem low/medium/high: "Muito alto" e "Máximo" viram high). Nos outros Gemini é ignorado.
export const EFFORT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'low', label: 'Baixo' },
  { value: 'medium', label: 'Médio' },
  { value: 'high', label: 'Alto' },
  { value: 'xhigh', label: 'Muito alto' },
  { value: 'max', label: 'Máximo' },
]

export interface PhaseDef {
  key: 'phase1' | 'phase2' | 'phase3' | 'biweekly' | 'trends' | 'summary'
  label: string
  defaultModel: string
  defaultMaxTokens: number
  defaultEffort: string
}

// Defaults espelham os do backend (getPhaseConfig) — comportamento atual.
export const PHASES: PhaseDef[] = [
  { key: 'phase1', label: 'Fase 1 — análise + ações', defaultModel: 'claude-sonnet-4-6', defaultMaxTokens: 8192, defaultEffort: 'high' },
  { key: 'phase2', label: 'Fase 2 — análise aprofundada (IA)', defaultModel: 'claude-sonnet-4-6', defaultMaxTokens: 4096, defaultEffort: 'high' },
  { key: 'phase3', label: 'Fase 3 — ações dos achados do gerente', defaultModel: 'claude-sonnet-4-6', defaultMaxTokens: 4096, defaultEffort: 'high' },
  { key: 'biweekly', label: 'Quinzenal — relatório do cliente', defaultModel: 'claude-sonnet-4-6', defaultMaxTokens: 4096, defaultEffort: 'high' },
  { key: 'trends', label: 'Tendências — análise histórica', defaultModel: 'claude-sonnet-4-6', defaultMaxTokens: 8192, defaultEffort: 'high' },
  { key: 'summary', label: 'Resumo — memória de longo prazo', defaultModel: 'gemini-3.8-flash', defaultMaxTokens: 4096, defaultEffort: 'high' },
]

// Defaults do chat "Falar sobre" — espelham src/services/chat.ts.
export const CHAT_DEFAULTS = {
  model: 'gemini-3.8-flash',
  effort: 'high',
  maxTokens: 8192,
  maxToolSteps: 6,
} as const
